/** @typedef {{ page: number, limit: number, sort: string, colors: string[], sizes: string[], minPrice?: number, maxPrice?: number, sale: boolean, tag: string|null, keyword?: string, dept?: string|null }} ShopPlpState */

export const PLP_PAGE_SIZE = 24;

/** Preset price buckets (USD); labels are i18n keys under modamai.shop.price* */
export const PRICE_BUCKET_DEFS = [
  { id: 'under-100', min: 0, max: 100, labelKey: 'priceUnder100' },
  { id: '100-200', min: 100, max: 200, labelKey: 'price100200' },
  { id: '200-400', min: 200, max: 400, labelKey: 'price200400' },
  { id: '400-600', min: 400, max: 600, labelKey: 'price400600' },
  { id: 'over-600', min: 600, max: null, labelKey: 'priceOver600' },
];

/**
 * @param {URLSearchParams} searchParams
 * @returns {ShopPlpState}
 */
export function parseShopPlpSearchParams(searchParams) {
  const pageRaw = parseInt(String(searchParams.get('page') || '1'), 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const sortUi = searchParams.get('sort') || 'recommended';
  const sort =
    sortUi === 'price-low' || sortUi === 'price-high' || sortUi === 'newest' ? sortUi : 'recommended';

  const colors = (searchParams.get('colors') || '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);

  const sizes = (searchParams.get('sizes') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const minRaw = searchParams.get('minPrice');
  const maxRaw = searchParams.get('maxPrice');
  const minPrice = minRaw != null && minRaw !== '' ? parseFloat(minRaw) : undefined;
  const maxPrice = maxRaw != null && maxRaw !== '' ? parseFloat(maxRaw) : undefined;

  const sale = searchParams.get('sale') === 'true';
  const tagRaw = searchParams.get('tag');
  const tag = tagRaw && /^[a-z0-9-]+$/.test(tagRaw) ? tagRaw.toLowerCase() : null;

  return {
    page,
    limit: PLP_PAGE_SIZE,
    sort,
    colors,
    sizes,
    minPrice: Number.isFinite(minPrice) ? minPrice : undefined,
    maxPrice: Number.isFinite(maxPrice) ? maxPrice : undefined,
    sale,
    tag,
  };
}

export function shopSortToApi(sortUi) {
  switch (sortUi) {
    case 'price-low':
      return 'price-low';
    case 'price-high':
      return 'price-high';
    case 'newest':
    case 'recommended':
    default:
      return 'newest';
  }
}

/**
 * @param {ShopPlpState} plp
 * @param {Record<string, unknown>} categoryExtra from buildShopProductApiParams
 */
export function buildShopPlpApiParams(plp, categoryExtra = {}) {
  /** @type {Record<string, string | number | boolean>} */
  const params = {
    page: plp.page,
    limit: plp.limit,
    sort: shopSortToApi(plp.sort),
    ...categoryExtra,
  };

  if (plp.tag) params.tag = plp.tag;
  if (plp.colors.length) params.colors = plp.colors.join(',');
  if (plp.sizes.length) params.sizes = plp.sizes.join(',');
  if (plp.minPrice != null) params.minPrice = plp.minPrice;
  if (plp.maxPrice != null) params.maxPrice = plp.maxPrice;
  if (plp.sale) params.sale = 'true';

  return params;
}

/**
 * Catalog PLP (search / sale): adds `q` and optional department `dept` on top of shop params.
 * @param {URLSearchParams} searchParams
 */
export function parseCatalogPlpSearchParams(searchParams) {
  const base = parseShopPlpSearchParams(searchParams);
  const keyword = String(searchParams.get('q') || '').trim();
  const deptRaw = String(searchParams.get('dept') || searchParams.get('category') || '')
    .trim()
    .toLowerCase();
  const dept = deptRaw && deptRaw !== 'all' ? deptRaw : null;
  return { ...base, keyword, dept };
}

/**
 * @param {ShopPlpState & { keyword?: string, dept?: string|null }} plp
 * @param {Record<string, unknown>} [extra]
 */
export function buildCatalogPlpApiParams(plp, extra = {}) {
  const params = buildShopPlpApiParams(plp, extra);
  if (plp.keyword) params.keyword = plp.keyword;
  if (plp.dept) params.category = plp.dept;
  return params;
}

/**
 * @param {URLSearchParams} prev
 * @param {Partial<ShopPlpState & { keyword?: string, dept?: string|null, resetPage?: boolean }>} patch
 */
export function patchCatalogPlpSearchParams(prev, patch) {
  const next = patchShopPlpSearchParams(prev, patch);
  const { keyword, dept } = patch;

  if (keyword !== undefined) {
    if (keyword) next.set('q', keyword);
    else next.delete('q');
  }
  if (dept !== undefined) {
    if (dept) next.set('dept', dept);
    else next.delete('dept');
  }
  return next;
}

/**
 * @param {URLSearchParams} prev
 * @param {Partial<ShopPlpState>} patch
 * @param {{ resetPage?: boolean }} [opts]
 */
export function patchShopPlpSearchParams(prev, patch) {
  const next = new URLSearchParams(prev);
  const resetPage = patch.resetPage !== false;
  const { resetPage: _rp, ...fields } = patch;

  if (fields.sort != null) {
    if (fields.sort === 'recommended') next.delete('sort');
    else next.set('sort', fields.sort);
  }

  if (fields.colors != null) {
    if (fields.colors.length) next.set('colors', fields.colors.join(','));
    else next.delete('colors');
  }

  if (fields.sizes != null) {
    if (fields.sizes.length) next.set('sizes', fields.sizes.join(','));
    else next.delete('sizes');
  }

  if (fields.minPrice !== undefined || fields.maxPrice !== undefined) {
    if (fields.minPrice != null) next.set('minPrice', String(fields.minPrice));
    else next.delete('minPrice');
    if (fields.maxPrice != null) next.set('maxPrice', String(fields.maxPrice));
    else next.delete('maxPrice');
  }

  if (fields.sale != null) {
    if (fields.sale) next.set('sale', 'true');
    else next.delete('sale');
  }

  if (fields.tag !== undefined) {
    if (fields.tag) next.set('tag', fields.tag);
    else next.delete('tag');
  }

  if (fields.page != null) {
    if (fields.page <= 1) next.delete('page');
    else next.set('page', String(fields.page));
  } else if (resetPage) {
    next.delete('page');
  }

  return next;
}

/** @param {ShopPlpState} plp */
export function countActivePlpFilters(plp) {
  let n = 0;
  if (plp.colors.length) n += plp.colors.length;
  if (plp.sizes.length) n += plp.sizes.length;
  if (plp.minPrice != null || plp.maxPrice != null) n += 1;
  if (plp.sale) n += 1;
  return n;
}

/** @param {ShopPlpState} plp @param {string} bucketId */
export function plpMatchesPriceBucket(plp, bucketId) {
  const def = PRICE_BUCKET_DEFS.find((b) => b.id === bucketId);
  if (!def) return false;
  const minOk = plp.minPrice == null ? def.min === 0 : plp.minPrice === def.min;
  const maxOk =
    def.max == null
      ? plp.maxPrice == null
      : plp.maxPrice === def.max && plp.minPrice === def.min;
  if (def.max == null) {
    return plp.minPrice === def.min && plp.maxPrice == null;
  }
  return minOk && maxOk && plp.minPrice === def.min && plp.maxPrice === def.max;
}

export function priceBucketToRange(bucketId) {
  const def = PRICE_BUCKET_DEFS.find((b) => b.id === bucketId);
  if (!def) return { minPrice: undefined, maxPrice: undefined };
  return {
    minPrice: def.min,
    maxPrice: def.max == null ? undefined : def.max,
  };
}
